import React, { useState, useRef, useCallback, useEffect } from 'react';
import { ArrowLeft, ArrowRight, RotateCcw, Home, Globe, Play, Square, AlertTriangle } from 'lucide-react';

// Agent interaction interface
export interface BrowserAgent {
  navigate: (url: string) => Promise<boolean>;
  clickElement: (selector: string) => Promise<boolean>;
  fillInput: (selector: string, value: string) => Promise<boolean>;
  getText: (selector: string) => Promise<string>;
  executeScript: (script: string) => Promise<any>;
  takeScreenshot: () => Promise<string>;
  waitForElement: (selector: string, timeout?: number) => Promise<boolean>;
  getCurrentUrl: () => string;
  getPageTitle: () => string;
}

const WebBrowserTab: React.FC = () => {
  const [currentUrl, setCurrentUrl] = useState('https://httpbin.org/html');
  const [inputUrl, setInputUrl] = useState('https://httpbin.org/html');
  const [isLoading, setIsLoading] = useState(false);
  const [canGoBack, setCanGoBack] = useState(false);
  const [canGoForward, setCanGoForward] = useState(false);
  const [pageTitle, setPageTitle] = useState('');
  const [history, setHistory] = useState<string[]>(['https://httpbin.org/html']);
  const [historyIndex, setHistoryIndex] = useState(0);
  const [corsError, setCorsError] = useState(false);
  const [agentMode, setAgentMode] = useState(false);
  const [agentLogs, setAgentLogs] = useState<string[]>([]);

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const agentTimeoutRef = useRef<number | null>(null);

  // Agent interaction API
  const browserAgent: BrowserAgent = {
    navigate: async (url: string): Promise<boolean> => {
      try {
        addAgentLog(`Navigating to: ${url}`);
        await navigateToUrl(url);
        return true;
      } catch (error) {
        addAgentLog(`Navigation failed: ${error}`);
        return false;
      }
    },

    clickElement: async (selector: string): Promise<boolean> => {
      try {
        addAgentLog(`Clicking element: ${selector}`);
        const result = await executeInFrame(`
          const element = document.querySelector('${selector}');
          if (element) {
            element.click();
            return true;
          }
          return false;
        `);
        addAgentLog(`Click ${result ? 'successful' : 'failed'}`);
        return result;
      } catch (error) {
        addAgentLog(`Click failed: ${error}`);
        return false;
      }
    },

    fillInput: async (selector: string, value: string): Promise<boolean> => {
      try {
        addAgentLog(`Filling input ${selector} with: ${value}`);
        const result = await executeInFrame(`
          const element = document.querySelector('${selector}');
          if (element && (element.tagName === 'INPUT' || element.tagName === 'TEXTAREA')) {
            element.value = '${value}';
            element.dispatchEvent(new Event('input', { bubbles: true }));
            element.dispatchEvent(new Event('change', { bubbles: true }));
            return true;
          }
          return false;
        `);
        addAgentLog(`Fill ${result ? 'successful' : 'failed'}`);
        return result;
      } catch (error) {
        addAgentLog(`Fill failed: ${error}`);
        return false;
      }
    },

    getText: async (selector: string): Promise<string> => {
      try {
        const result = await executeInFrame(`
          const element = document.querySelector('${selector}');
          return element ? element.textContent || element.innerText || '' : '';
        `);
        addAgentLog(`Got text from ${selector}: "${result}"`);
        return result || '';
      } catch (error) {
        addAgentLog(`Get text failed: ${error}`);
        return '';
      }
    },

    executeScript: async (script: string): Promise<any> => {
      try {
        addAgentLog(`Executing script: ${script.substring(0, 100)}${script.length > 100 ? '...' : ''}`);
        const result = await executeInFrame(script);
        addAgentLog(`Script executed successfully`);
        return result;
      } catch (error) {
        addAgentLog(`Script execution failed: ${error}`);
        return null;
      }
    },

    takeScreenshot: async (): Promise<string> => {
      addAgentLog('Screenshot not available in web context');
      return '';
    },

    waitForElement: async (selector: string, timeout: number = 5000): Promise<boolean> => {
      try {
        addAgentLog(`Waiting for element: ${selector}`);
        const result = await executeInFrame(`
          return new Promise((resolve) => {
            const startTime = Date.now();
            const checkElement = () => {
              const element = document.querySelector('${selector}');
              if (element) {
                resolve(true);
              } else if (Date.now() - startTime > ${timeout}) {
                resolve(false);
              } else {
                setTimeout(checkElement, 100);
              }
            };
            checkElement();
          });
        `);
        addAgentLog(`Wait for element ${result ? 'successful' : 'timed out'}`);
        return result;
      } catch (error) {
        addAgentLog(`Wait for element failed: ${error}`);
        return false;
      }
    },

    getCurrentUrl: (): string => currentUrl,
    getPageTitle: (): string => pageTitle
  };

  // Expose agent to global scope for external access
  useEffect(() => {
    (window as any).browserAgent = browserAgent;
    return () => {
      delete (window as any).browserAgent;
    };
  }, []);

  const addAgentLog = (message: string) => {
    const timestamp = new Date().toLocaleTimeString();
    setAgentLogs(prev => [...prev.slice(-19), `[${timestamp}] ${message}`]);
  };

  const executeInFrame = async (script: string): Promise<any> => {
    return new Promise((resolve, reject) => {
      if (!iframeRef.current) {
        reject('Iframe not available');
        return;
      }

      try {
        const iframe = iframeRef.current;
        const contentWindow = iframe.contentWindow as any;
        if (contentWindow && contentWindow.eval) {
          const result = contentWindow.eval(script);
          resolve(result);
        } else {
          reject('Cannot access iframe content window');
        }
      } catch (error) {
        if (error instanceof Error && error.message.includes('cross-origin')) {
          reject('Cross-origin restriction - cannot access iframe content');
        } else {
          reject(error);
        }
      }
    });
  };

  const navigateToUrl = useCallback(async (url: string) => {
    setIsLoading(true);
    setCorsError(false);
    
    // Ensure URL has protocol
    const fullUrl = url.startsWith('http') ? url : `https://${url}`;
    
    try {
      setCurrentUrl(fullUrl);
      setInputUrl(fullUrl);
      
      // Update history
      const newHistory = [...history.slice(0, historyIndex + 1), fullUrl];
      setHistory(newHistory);
      setHistoryIndex(newHistory.length - 1);
      
      setCanGoBack(historyIndex > 0 || newHistory.length > 1);
      setCanGoForward(false);
      
      if (iframeRef.current) {
        iframeRef.current.src = fullUrl;
      }
    } catch (error) {
      console.error('Navigation error:', error);
    }
  }, [history, historyIndex]);

  const handleGoBack = () => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      const url = history[newIndex];
      setCurrentUrl(url);
      setInputUrl(url);
      setCanGoBack(newIndex > 0);
      setCanGoForward(true);
      if (iframeRef.current) {
        iframeRef.current.src = url;
      }
    }
  };

  const handleGoForward = () => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      const url = history[newIndex];
      setCurrentUrl(url);
      setInputUrl(url);
      setCanGoBack(true);
      setCanGoForward(newIndex < history.length - 1);
      if (iframeRef.current) {
        iframeRef.current.src = url;
      }
    }
  };

  const handleRefresh = () => {
    if (iframeRef.current) {
      iframeRef.current.src = currentUrl;
    }
  };

  const handleHome = () => {
    navigateToUrl('https://httpbin.org/html');
  };

  const handleUrlSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    navigateToUrl(inputUrl);
  };

  const handleIframeLoad = () => {
    setIsLoading(false);
    try {
      if (iframeRef.current?.contentDocument) {
        const title = iframeRef.current.contentDocument.title;
        setPageTitle(title);
      }
    } catch (error) {
      // Cross-origin restriction - can't access title
      setPageTitle('Website');
    }
  };

  const handleIframeError = () => {
    setIsLoading(false);
    setCorsError(true);
  };

  return (
    <div className="w-full h-full bg-engramiq-dark-blue flex flex-col">
      {/* Browser Controls */}
      <div className="bg-engramiq-dark-blue border-b border-engramiq-green/20 p-3 flex-shrink-0">
        <div className="flex items-center gap-2 mb-3">
          {/* Navigation Controls */}
          <button
            onClick={handleGoBack}
            disabled={!canGoBack}
            className="p-2 text-engramiq-light-grey hover:text-engramiq-green disabled:opacity-50 disabled:cursor-not-allowed"
            title="Go back"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          
          <button
            onClick={handleGoForward}
            disabled={!canGoForward}
            className="p-2 text-engramiq-light-grey hover:text-engramiq-green disabled:opacity-50 disabled:cursor-not-allowed"
            title="Go forward"
          >
            <ArrowRight className="w-4 h-4" />
          </button>
          
          <button
            onClick={handleRefresh}
            className="p-2 text-engramiq-light-grey hover:text-engramiq-green"
            title="Refresh"
          >
            <RotateCcw className="w-4 h-4" />
          </button>
          
          <button
            onClick={handleHome}
            className="p-2 text-engramiq-light-grey hover:text-engramiq-green"
            title="Home"
          >
            <Home className="w-4 h-4" />
          </button>

          {/* Address Bar */}
          <form onSubmit={handleUrlSubmit} className="flex-1 flex">
            <div className="relative flex-1 max-w-2xl">
              <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-engramiq-bright-grey" />
              <input
                type="text"
                value={inputUrl}
                onChange={(e) => setInputUrl(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-engramiq-dark-blue border border-engramiq-green/30 rounded text-engramiq-light-grey placeholder-engramiq-bright-grey focus:outline-none focus:border-engramiq-green"
                placeholder="Enter URL..."
              />
            </div>
          </form>

          {/* Agent Mode Toggle */}
          <button
            onClick={() => setAgentMode(!agentMode)}
            className={`p-2 ${agentMode ? 'text-engramiq-green' : 'text-engramiq-light-grey'} hover:text-engramiq-green`}
            title="Toggle Agent Mode"
          >
            {agentMode ? <Square className="w-4 h-4" /> : <Play className="w-4 h-4" />}
          </button>
        </div>

        {/* Page Info */}
        <div className="text-xs text-engramiq-bright-grey flex items-center gap-2">
          <span>{pageTitle || 'Web Browser'}</span>
          {isLoading && <span>Loading...</span>}
          {corsError && (
            <div className="flex items-center gap-1 text-yellow-400">
              <AlertTriangle className="w-3 h-3" />
              <span>CORS restricted</span>
            </div>
          )}
        </div>
      </div>

      <div className="flex-1 flex">
        {/* Main Browser Area */}
        <div className={`${agentMode ? 'flex-1' : 'w-full'} relative bg-white`}>
          <iframe
            ref={iframeRef}
            src={currentUrl}
            className="w-full h-full border-none"
            onLoad={handleIframeLoad}
            onError={handleIframeError}
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals"
            title="Embedded Browser"
          />
          
          {corsError && (
            <div className="absolute inset-0 bg-engramiq-dark-blue flex items-center justify-center">
              <div className="text-center p-8">
                <AlertTriangle className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
                <h3 className="text-engramiq-light-grey font-medium mb-2">Content Blocked</h3>
                <p className="text-engramiq-bright-grey text-sm mb-4">
                  This website blocks iframe embedding (X-Frame-Options or CSP).
                </p>
                <p className="text-engramiq-bright-grey text-xs mb-3">
                  Many sites like Google, Facebook, and banking sites block embedding for security.
                </p>
                <button
                  onClick={() => navigateToUrl('https://httpbin.org/html')}
                  className="px-3 py-1 bg-engramiq-green/20 text-engramiq-green rounded text-xs hover:bg-engramiq-green/30"
                >
                  Try iframe-friendly site
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Agent Control Panel */}
        {agentMode && (
          <div className="w-80 bg-engramiq-dark-blue border-l border-engramiq-green/20 flex flex-col">
            <div className="p-3 border-b border-engramiq-green/20">
              <h3 className="text-sm font-bold text-engramiq-light-grey mb-2">Agent Control</h3>
              <div className="text-xs text-engramiq-bright-grey">
                Browser agent is available at <code className="text-engramiq-green">window.browserAgent</code>
              </div>
            </div>
            
            <div className="flex-1 overflow-hidden">
              <div className="p-3">
                <h4 className="text-xs font-semibold text-engramiq-green mb-2">Agent Logs</h4>
                <div className="h-40 bg-black/20 border border-engramiq-green/20 rounded p-2 overflow-y-auto">
                  {agentLogs.length === 0 ? (
                    <div className="text-xs text-engramiq-bright-grey">No agent activity yet</div>
                  ) : (
                    agentLogs.map((log, index) => (
                      <div key={index} className="text-xs text-engramiq-light-grey mb-1 font-mono">
                        {log}
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div className="p-3 border-t border-engramiq-green/20">
                <h4 className="text-xs font-semibold text-engramiq-green mb-2">Iframe-Friendly URLs</h4>
                <div className="text-xs text-engramiq-bright-grey space-y-1 mb-3">
                  <button 
                    onClick={() => navigateToUrl('https://httpbin.org/html')}
                    className="block w-full text-left hover:text-engramiq-light-grey"
                  >
                    httpbin.org/html
                  </button>
                  <button 
                    onClick={() => navigateToUrl('https://httpbin.org/forms/post')}
                    className="block w-full text-left hover:text-engramiq-light-grey"
                  >
                    httpbin.org/forms/post
                  </button>
                  <button 
                    onClick={() => navigateToUrl('https://www.w3schools.com/html/tryit.asp?filename=tryhtml_basic')}
                    className="block w-full text-left hover:text-engramiq-light-grey"
                  >
                    W3Schools Tryit Editor
                  </button>
                </div>
                
                <h4 className="text-xs font-semibold text-engramiq-green mb-2">Agent Methods</h4>
                <div className="text-xs text-engramiq-bright-grey space-y-1">
                  <div><code>navigate(url)</code></div>
                  <div><code>clickElement(selector)</code></div>
                  <div><code>fillInput(selector, value)</code></div>
                  <div><code>getText(selector)</code></div>
                  <div><code>executeScript(script)</code></div>
                  <div><code>waitForElement(selector)</code></div>
                  <div><code>getCurrentUrl()</code></div>
                  <div><code>getPageTitle()</code></div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WebBrowserTab;